// Tenant Settings Service - Handles tenant-wide settings including profile deadline configuration

import { apiGet, apiPatch } from '@/utils/api';

export type DeadlineUnit = 'DAY' | 'WEEK' | 'MONTH';

export interface TenantSettings {
  id: string;
  tenantId: string;
  profileSubmissionDeadlineValue: number;
  profileSubmissionDeadlineUnit: DeadlineUnit;
  profileDeadlineEnforced: boolean;
  autoSubmitProfileEnabled: boolean;
  financeModuleEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DeadlineSettings {
  profileSubmissionDeadlineValue: number;
  profileSubmissionDeadlineUnit: DeadlineUnit;
  profileDeadlineEnforced: boolean;
}

export interface DeadlineStatus {
  isPastDeadline: boolean;
  deadlineDate: string | null;
  daysRemaining: number | null;
  firstLoginAt: string | null;
  isBlocked: boolean;
  hasSubmittedProfile: boolean;
}

export interface UpdateDeadlineSettingsRequest {
  profileSubmissionDeadlineValue?: number;
  profileSubmissionDeadlineUnit?: DeadlineUnit;
  profileDeadlineEnforced?: boolean;
}

export const tenantSettingsService = {
  /**
   * Get all tenant settings
   */
  async getSettings(): Promise<TenantSettings> {
    return apiGet<TenantSettings>('/settings');
  },

  /**
   * Get profile deadline settings specifically
   */
  async getDeadlineSettings(): Promise<DeadlineSettings> {
    return apiGet<DeadlineSettings>('/settings/profile-deadline');
  },

  /**
   * Update profile deadline settings
   */
  async updateDeadlineSettings(data: UpdateDeadlineSettingsRequest): Promise<TenantSettings> {
    return apiPatch<TenantSettings>('/settings/profile-deadline', data);
  },

  /**
   * Get current user's deadline status
   */
  async getMyDeadlineStatus(): Promise<DeadlineStatus> {
    return apiGet<DeadlineStatus>('/settings/my-deadline-status');
  },

  /**
   * Get deadline status for a specific user (admin only)
   */
  async getUserDeadlineStatus(userId: string): Promise<DeadlineStatus> {
    return apiGet<DeadlineStatus>(`/settings/user/${userId}/deadline-status`);
  },
};

export default tenantSettingsService;
